import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Button, Typography, Alert, List, ListItem, ListItemText,
  IconButton, RadioGroup, FormControlLabel, Radio, FormControl, FormLabel,
  LinearProgress, Chip, Divider, Checkbox,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import TagIcon from '@mui/icons-material/Tag';
import InfoIcon from '@mui/icons-material/Info';

const MAX_TOTAL_BYTES = 500 * 1024 * 1024;

function UploadPanel({
  files, onFilesChange, fileError,
  dataType, onDataTypeChange,
  normalizePhone, onNormalizePhoneChange,
  normalizeEmail, onNormalizeEmailChange,
  exportMode, onExportModeChange,
  columnMapping,
  onHashClick,
  processing, progress, currentFile,
}) {
  const inputRef = useRef(null);
  const [localError, setLocalError] = useState(null);
  const [removedFileInfo, setRemovedFileInfo] = useState(false);

  const isGated = !dataType;

  const handleFiles = (incoming) => {
    if (isGated || processing) return;
    setLocalError(null);
    setRemovedFileInfo(false);
    const arr = Array.from(incoming);
    const nonCsv = arr.filter((f) => !f.name.toLowerCase().endsWith('.csv'));
    if (nonCsv.length > 0) {
      setLocalError('CSV files only — please upload .csv files.');
      return;
    }
    const merged = [...files, ...arr];
    const totalSize = merged.reduce((acc, f) => acc + f.size, 0);
    onFilesChange(merged, totalSize > MAX_TOTAL_BYTES ? 'Total file size exceeds 500 MB' : null);
  };

  const removeFile = (index) => {
    const next = files.filter((_, i) => i !== index);
    const totalSize = next.reduce((acc, f) => acc + f.size, 0);
    onFilesChange(next, totalSize > MAX_TOTAL_BYTES ? 'Total file size exceeds 500 MB' : null);
    setRemovedFileInfo(true);
  };

  const displayError = fileError || localError;
  const allColumnsMapped = files.length > 0 && files.every((f) => columnMapping[f.name]);
  const canHash = files.length > 0 && !fileError && allColumnsMapped && !processing && !isGated;

  return (
    <Box>
      {/* Data Type — always interactive; everything else is gated on this */}
      <FormControl sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', mb: 1 }}>
          <FormLabel sx={{ mr: 1 }} id="data-type-label">
            <Typography>Data Type</Typography>
          </FormLabel>
          <Tooltip
            title="Select the type of data you are hashing. This is required to enable file upload."
            slotProps={{
              tooltip: {
                sx: {
                  fontSize: '1rem',
                },
              },
            }}
          >
            <InfoIcon sx={{ fill: '#5e6262' }} />
          </Tooltip>
        </Box>
        <RadioGroup
          row
          aria-labelledby="data-type-label"
          value={dataType || ''}
          onChange={(e) => onDataTypeChange(e.target.value)}
        >
          <FormControlLabel value="email" control={<Radio />} label="Email" disabled={processing} />
          <FormControlLabel value="phone" control={<Radio />} label="Phone Number" disabled={processing} />
        </RadioGroup>
      </FormControl>

      <Divider sx={{ my: 2 }} />

      {isGated && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Select a data type above to enable file upload.
        </Alert>
      )}

      {/* Everything below requires a data type to be selected first */}
      <Box sx={{ opacity: isGated ? 0.5 : 1, pointerEvents: isGated ? 'none' : 'auto' }}>
        {/* Drop Zone */}
        <Box
          role="button"
          tabIndex={0}
          aria-label="Upload CSV files"
          onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => !processing && !isGated && inputRef.current?.click()}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !processing && !isGated) {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
          sx={{
            border: '2px dashed',
            borderColor: displayError ? 'error.main' : 'primary.main',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            cursor: (processing || isGated) ? 'default' : 'pointer',
            mb: 2,
            '&:hover': (processing || isGated) ? {} : { bgcolor: 'action.hover' },
          }}
        >
          <UploadFileIcon sx={{ fontSize: 48, color: 'primary.main' }} />
          <Typography variant="h6" sx={{ mt: 1 }}>
            Drag &amp; drop CSV files here or click to upload
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Accepts .csv files — max 500 MB total
          </Typography>
          <input
            ref={inputRef}
            type="file"
            accept=".csv"
            multiple
            disabled={isGated || processing}
            style={{ display: 'none' }}
            onChange={(e) => handleFiles(e.target.files)}
          />
        </Box>

        {displayError && <Alert severity="error" sx={{ mb: 2 }}>{displayError}</Alert>}
        {removedFileInfo && (
          <Alert severity="info" sx={{ mb: 2 }}>
            To re-add a removed file, select it again using the drop zone above.
            If it doesn&apos;t appear after clicking, try dragging and dropping it instead.
          </Alert>
        )}

        {/* File List */}
        {files.length > 0 && (
          <List dense sx={{ mb: 1 }}>
            {/* eslint-disable react/no-array-index-key */}
            {files.map((f, i) => (
              <ListItem
                key={`${f.name}-${i}`}
                secondaryAction={(
                  <IconButton edge="end" aria-label="remove file" onClick={() => removeFile(i)} disabled={processing}>
                    <DeleteIcon />
                  </IconButton>
                )}
              >
                <ListItemText
                  primary={f.name}
                  secondary={columnMapping[f.name]
                    ? <Chip icon={<TagIcon />} label={`Column: ${columnMapping[f.name]}`} size="small" variant="outlined" />
                    : <Chip label="Column not detected" size="small" color="warning" variant="outlined" />}
                />
              </ListItem>
            ))}
            {/* eslint-enable react/no-array-index-key */}
          </List>
        )}

        {files.length > 0 && !processing && (
          <Button variant="outlined" size="small" onClick={() => inputRef.current?.click()} sx={{ mb: 2 }}>
            Add more files
          </Button>
        )}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {/* Phone normalization checkbox — only when phone is selected */}
          {dataType === 'phone' && (
            <Box sx={{ mb: 2, ml: 1 }}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={normalizePhone}
                    onChange={(e) => onNormalizePhoneChange(e.target.checked)}
                    disabled={processing}
                  />
                )}
                label="Normalize phone numbers before hashing"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                {/* eslint-disable-next-line max-len */}
                Strips non-numeric characters and validates 10-digit US format. Invalid rows are discarded.
              </Typography>
            </Box>
          )}

          {/* Email normalization checkbox — only when email is selected */}
          {dataType === 'email' && (
            <Box sx={{ mb: 2, ml: 1 }}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={normalizeEmail}
                    onChange={(e) => onNormalizeEmailChange(e.target.checked)}
                    disabled={processing}
                  />
                )}
                label="Normalize emails before hashing"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                {/* eslint-disable-next-line max-len */}
                Trims whitespace and lowercases the address. Left unchecked, emails are hashed exactly as entered.
              </Typography>
            </Box>
          )}

          {/* Merge/Separate — only shown for multi-file */}
          {files.length > 1 && (
            <FormControl sx={{ mb: 2 }}>
              <FormLabel id="export-mode-label">
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Export Mode</Typography>
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="export-mode-label"
                value={exportMode}
                onChange={(e) => onExportModeChange(e.target.value)}
              >
                <FormControlLabel value="merge" control={<Radio />} label="Merge into one file" disabled={processing || isGated} />
                <FormControlLabel value="separate" control={<Radio />} label="Export as separate files" disabled={processing || isGated} />
              </RadioGroup>
            </FormControl>
          )}

          {/* Progress */}
          {processing && (
            <Box sx={{ mb: 2 }}>
              {currentFile && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
                  Processing: {currentFile}
                </Typography>
              )}
              <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {progress}
                %
              </Typography>
            </Box>
          )}

          {/* Hash Button */}
          <Button
            variant="contained"
            size="large"
            onClick={onHashClick}
            disabled={!canHash}
            sx={{ mt: 1, width: 'fit-content' }}
          >
            {files.length > 1 && exportMode === 'separate' ? 'Hash Files' : 'Hash File'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

UploadPanel.propTypes = {
  files: PropTypes.arrayOf(PropTypes.instanceOf(File)).isRequired,
  onFilesChange: PropTypes.func.isRequired,
  fileError: PropTypes.string,
  dataType: PropTypes.oneOf(['email', 'phone']),
  onDataTypeChange: PropTypes.func.isRequired,
  normalizePhone: PropTypes.bool.isRequired,
  onNormalizePhoneChange: PropTypes.func.isRequired,
  normalizeEmail: PropTypes.bool.isRequired,
  onNormalizeEmailChange: PropTypes.func.isRequired,
  exportMode: PropTypes.oneOf(['merge', 'separate']).isRequired,
  onExportModeChange: PropTypes.func.isRequired,
  columnMapping: PropTypes.objectOf(PropTypes.string).isRequired,
  onHashClick: PropTypes.func.isRequired,
  processing: PropTypes.bool.isRequired,
  progress: PropTypes.number.isRequired,
  currentFile: PropTypes.string,
};

export default UploadPanel;
